import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scan, Gavel, ArrowRight, CheckCircle } from 'lucide-react';
import { slideUp, staggerContainer } from '../utils/animations';
const steps = [{
  icon: FileText,
  title: 'Register Your Trademarks',
  description: 'Input your trademark registration numbers. We verify status with USPTO, WIPO, and other global registries.',
  color: 'text-blue-400',
  bg: 'bg-blue-500/10',
  border: 'border-blue-500/20',
  features: ['USPTO Integration', 'WIPO Verification', 'Multi-jurisdiction Support']
}, {
  icon: Scan,
  title: 'Automated Monitoring',
  description: 'Our system scans 24/7 for unauthorized use, counterfeits, and lookalike domains across the web.',
  color: 'text-indigo-400',
  bg: 'bg-indigo-500/10',
  border: 'border-indigo-500/20',
  features: ['Real-time Scanning', '7 Channel Coverage', 'AI-Powered Detection']
}, {
  icon: Gavel,
  title: 'Legal Enforcement',
  description: 'Generate and send cease-and-desist notices or platform takedown requests with one click.',
  color: 'text-amber-400',
  bg: 'bg-amber-500/10',
  border: 'border-amber-500/20',
  features: ['Evidence Collection', 'Legal Templates', 'Automated Submission']
}];
export function HowItWorks() {
  return <section className="py-20 md:py-28 lg:py-32 bg-slate-900 text-white relative overflow-hidden" data-id="element-227">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-228"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-transparent to-slate-900" data-id="element-229"></div>

      {/* Animated gradient orbs */}
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 8,
      repeat: Infinity
    }} className="absolute top-1/4 left-1/4 w-64 h-64 bg-indigo-500/30 rounded-full blur-[100px]" data-id="element-230" />
      <motion.div animate={{
      scale: [1, 1.3, 1],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      delay: 2
    }} className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-amber-500/20 rounded-full blur-[100px]" data-id="element-231" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-232">
        <div className="text-center mb-16 md:mb-20" data-id="element-233">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 tracking-tight" data-id="element-234">
            Trademark protection workflow
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={slideUp} className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-235">
            Streamline your IP enforcement from detection to resolution.
          </motion.p>
        </div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid md:grid-cols-3 gap-8 relative" data-id="element-236">
          {/* Connector Line (Desktop) */}
          <div className="hidden md:block absolute top-12 left-[16%] w-[68%] h-0.5 bg-slate-800 -z-10 transform -translate-y-1/2 overflow-hidden" data-id="element-237">
            <motion.div initial={{
            x: '-100%'
          }} whileInView={{
            x: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 2,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatDelay: 3
          }} className="h-full bg-gradient-to-r from-transparent via-indigo-400 to-transparent w-1/3" data-id="element-238" />
          </div>

          {steps.map((step, index) => <motion.div key={step.title} variants={slideUp} whileHover={{
          y: -10,
          transition: {
            duration: 0.3
          }
        }} className="relative flex flex-col items-center text-center group cursor-default" data-id="element-239">
              <motion.div whileHover={{
            scale: 1.1,
            rotate: 5
          }} className={`w-24 h-24 rounded-2xl ${step.bg} ${step.border} border flex items-center justify-center mb-8 shadow-xl relative z-10 transition-all duration-500`} data-id="element-240">
                <step.icon className={`w-10 h-10 ${step.color}`} data-id="element-241" />

                {/* Step Number Badge */}
                <motion.div whileHover={{
              scale: 1.2
            }} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-bold text-white shadow-lg" data-id="element-242">
                  {index + 1}
                </motion.div>

                {/* Pulse effect */}
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: index * 0.3
            }} className={`absolute inset-0 rounded-2xl ${step.bg} ${step.border} border`} data-id="element-243" />
              </motion.div>

              <h3 className="text-xl font-bold mb-4 group-hover:text-indigo-400 transition-colors" data-id="element-244">
                {step.title}
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-xs mb-6" data-id="element-245">
                {step.description}
              </p>

              {/* Feature list */}
              <div className="space-y-2" data-id="element-246">
                {step.features.map((feature, i) => <motion.div key={feature} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1 + i * 0.1
            }} className="flex items-center gap-2 text-sm text-slate-500" data-id="element-247">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-248" />
                    <span data-id="element-249">{feature}</span>
                  </motion.div>)}
              </div>

              {/* Mobile Connector */}
              {index < steps.length - 1 && <motion.div initial={{
            scaleY: 0
          }} whileInView={{
            scaleY: 1
          }} viewport={{
            once: true
          }} className="md:hidden h-12 w-0.5 bg-slate-800 my-4 origin-top" data-id="element-250" />}
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}